import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  APIProvider,
  Map,
  Marker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { Loader2, Search } from "lucide-react";
import useDebounce from "../../../hooks/useDebounce";
import { parseLatLng } from "./validation";

const DEFAULT_CENTER = { lat: 21.4925, lng: 39.17757 };

const mapsFailureListeners = new Set();
let mapsUnavailable = false;
let mapPickerActive = 0;

const isMapsScriptError = (event) => {
  const message = String(
    event?.message || event?.error?.message || event?.reason?.message || "",
  );
  const source = String(event?.filename || "");
  if (
    /3d context|BillingNotEnabled|maps\.googleapis|maps\.gstatic/i.test(
      `${message} ${source}`,
    )
  ) {
    return true;
  }
  // Cross-origin Maps failures arrive as "Script error." with no file.
  // The dev overlay listens in the bubble phase, so this has to be decided
  // while a picker is on the page, before that overlay runs.
  return message === "Script error." && !source && mapPickerActive > 0;
};

const notifyMapsUnavailable = () => {
  if (mapsUnavailable) return;
  mapsUnavailable = true;
  mapsFailureListeners.forEach((listener) => listener());
};

const installMapsErrorGuard = () => {
  if (typeof window === "undefined") return;
  const swallow = (event) => {
    if (!isMapsScriptError(event)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    notifyMapsUnavailable();
  };
  window.__ghorfaMapsSwallow = swallow;
  if (window.__ghorfaMapsErrorGuard) return;
  window.__ghorfaMapsErrorGuard = true;
  const previousAuthFailure = window.gm_authFailure;
  window.gm_authFailure = () => {
    notifyMapsUnavailable();
    if (typeof previousAuthFailure === "function" && previousAuthFailure !== window.gm_authFailure) {
      previousAuthFailure();
    }
  };
  const forward = (event) => window.__ghorfaMapsSwallow?.(event);
  window.addEventListener("error", forward, true);
  window.addEventListener("unhandledrejection", forward, true);
};

if (typeof window !== "undefined") installMapsErrorGuard();

const formatLatLng = (lat, lng) =>
  `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;

const MapViewSync = ({ lat, lng }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const current = map.getCenter();
    const sameCenter =
      current != null &&
      Math.abs(current.lat() - lat) < 0.00001 &&
      Math.abs(current.lng() - lng) < 0.00001;
    if (sameCenter) return;
    map.panTo({ lat, lng });
    if ((map.getZoom() ?? 0) < 16) map.setZoom(16);
  }, [lat, lng, map]);

  return null;
};

const MapClickHandler = ({ onPick }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;
    const listener = map.addListener("click", (event) => {
      if (!event.latLng) return;
      onPick(event.latLng.lat(), event.latLng.lng());
    });
    return () => listener.remove();
  }, [map, onPick]);

  return null;
};

const AddressSearch = ({ bias, onPick }) => {
  const placesLib = useMapsLibrary("places");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [placesDenied, setPlacesDenied] = useState(false);
  const requestId = useRef(0);
  const skipSearch = useRef(false);
  const biasRef = useRef(bias);
  biasRef.current = bias;
  const sessionToken = useRef(null);

  useEffect(() => {
    if (!placesLib || sessionToken.current) return;
    sessionToken.current = new placesLib.AutocompleteSessionToken();
  }, [placesLib]);

  const geocodeQuery = useCallback(
    (input) =>
      new Promise((resolve) => {
        if (!window.google?.maps?.Geocoder) {
          resolve([]);
          return;
        }
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          {
            address: input,
            componentRestrictions: { country: "SA" },
          },
          (results, status) => {
            if (status !== "OK" || !results?.length) {
              resolve([]);
              return;
            }
            resolve(
              results.slice(0, 5).map((result, index) => ({
                id: result.place_id || `geo-${index}`,
                placeId: result.place_id,
                label: result.formatted_address,
                lat: result.geometry.location.lat(),
                lng: result.geometry.location.lng(),
              })),
            );
          },
        );
      }),
    [],
  );

  const predictQuery = useCallback(
    (input) =>
      new Promise((resolve) => {
        if (!placesLib || placesDenied) {
          resolve(null);
          return;
        }
        const service = new placesLib.AutocompleteService();
        service.getPlacePredictions(
          {
            input,
            componentRestrictions: { country: "sa" },
            locationBias: {
              center: biasRef.current,
              radius: 50000,
            },
            sessionToken: sessionToken.current ?? undefined,
          },
          (predictions, status) => {
            if (
              status ===
              window.google?.maps?.places?.PlacesServiceStatus?.REQUEST_DENIED
            ) {
              setPlacesDenied(true);
              resolve(null);
              return;
            }
            if (
              status !==
                window.google?.maps?.places?.PlacesServiceStatus?.OK ||
              !predictions
            ) {
              resolve([]);
              return;
            }
            resolve(
              predictions.slice(0, 5).map((prediction) => ({
                id: prediction.place_id,
                placeId: prediction.place_id,
                label:
                  prediction.structured_formatting?.main_text ||
                  prediction.description,
                secondary: prediction.structured_formatting?.secondary_text,
              })),
            );
          },
        );
      }),
    [placesDenied, placesLib],
  );

  const runSearch = useCallback(
    async (input) => {
      const trimmed = input.trim();
      if (trimmed.length < 2) {
        setSuggestions([]);
        setOpen(false);
        return [];
      }

      const id = ++requestId.current;
      setSearching(true);
      try {
        const predictions = await predictQuery(trimmed);
        const next =
          predictions && predictions.length > 0
            ? predictions
            : await geocodeQuery(trimmed);
        if (id !== requestId.current) return next;
        setSuggestions(next);
        setOpen(true);
        return next;
      } finally {
        if (id === requestId.current) setSearching(false);
      }
    },
    [geocodeQuery, predictQuery],
  );

  useEffect(() => {
    if (skipSearch.current) {
      skipSearch.current = false;
      return undefined;
    }
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      runSearch(trimmed);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [query, runSearch]);

  const moveTo = (lat, lng, label) => {
    skipSearch.current = true;
    setQuery(label);
    setOpen(false);
    setSuggestions([]);
    onPick(lat, lng);
    if (placesLib) {
      sessionToken.current = new placesLib.AutocompleteSessionToken();
    }
  };

  const choose = (suggestion) => {
    if (
      suggestion.lat != null &&
      suggestion.lng != null &&
      Number.isFinite(suggestion.lat) &&
      Number.isFinite(suggestion.lng)
    ) {
      moveTo(suggestion.lat, suggestion.lng, suggestion.label);
      return;
    }
    if (!suggestion.placeId || !window.google?.maps?.Geocoder) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: suggestion.placeId }, (results, status) => {
      const location = results?.[0]?.geometry?.location;
      if (status !== "OK" || !location) return;
      moveTo(
        location.lat(),
        location.lng(),
        results?.[0]?.formatted_address || suggestion.label,
      );
    });
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              if (suggestions[0]) {
                choose(suggestions[0]);
                return;
              }
              runSearch(query).then((next) => {
                if (next[0]) choose(next[0]);
              });
            }}
            placeholder="Search an address, district, or landmark"
            className="w-full rounded-md border border-gray-400 bg-white py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-darkBg2 dark:text-[#c7d8eb]"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            runSearch(query).then((next) => {
              if (next.length === 1) choose(next[0]);
              else setOpen(true);
            });
          }}
          className="shrink-0 rounded-md bg-secondaryColor px-3 text-sm font-semibold text-white"
        >
          {searching ? <Loader2 size={16} className="animate-spin" /> : "Search"}
        </button>
      </div>
      {open && (
        <ul className="mt-1 max-h-60 overflow-auto rounded-md border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-darkBg2">
          {suggestions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">No results</li>
          ) : (
            suggestions.map((suggestion) => (
              <li key={suggestion.id}>
                <button
                  type="button"
                  onClick={() => choose(suggestion)}
                  className="flex w-full flex-col px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <span className="text-sm font-medium">{suggestion.label}</span>
                  {suggestion.secondary ? (
                    <span className="text-xs text-gray-500">
                      {suggestion.secondary}
                    </span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

const MapCanvas = ({ position, onPick }) => {
  const pick = useCallback(
    (lat, lng) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      onPick(formatLatLng(lat, lng));
    },
    [onPick],
  );

  return (
    <div className="h-72 w-full overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
      <Map
        style={{ width: "100%", height: "100%" }}
        defaultCenter={position}
        defaultZoom={13}
        gestureHandling="greedy"
        disableDefaultUI
        zoomControl
        fullscreenControl
        renderingType="RASTER"
      >
        <MapViewSync lat={position.lat} lng={position.lng} />
        <MapClickHandler onPick={pick} />
        <Marker
          position={position}
          draggable
          onDragEnd={(event) => {
            if (!event.latLng) return;
            pick(event.latLng.lat(), event.latLng.lng());
          }}
        />
      </Map>
    </div>
  );
};

const coordinateClass =
  "w-full rounded-md border border-gray-400 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-darkBg2 dark:text-[#c7d8eb]";

const ManualCoordinates = ({ value, onChange, message }) => (
  <div className="flex flex-col gap-2">
    <p className="text-sm text-amber-700 dark:text-amber-400">{message}</p>
    <input
      className={coordinateClass}
      value={value || ""}
      placeholder="21.492500, 39.177570"
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

const MapPinField = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const [unavailable, setUnavailable] = useState(mapsUnavailable);
  const countedRef = useRef(false);
  if (!countedRef.current) {
    countedRef.current = true;
    mapPickerActive += 1;
  }

  useEffect(() => {
    const onFail = () => setUnavailable(true);
    mapsFailureListeners.add(onFail);
    if (mapsUnavailable) setUnavailable(true);
    return () => {
      mapPickerActive = Math.max(0, mapPickerActive - 1);
      mapsFailureListeners.delete(onFail);
    };
  }, []);

  if (!apiKey) {
    return (
      <ManualCoordinates
        value={props.value}
        onChange={props.onChange}
        message="Google Maps is not configured for the admin app. Type the pin as latitude, longitude."
      />
    );
  }

  if (unavailable) {
    return (
      <ManualCoordinates
        value={props.value}
        onChange={props.onChange}
        message="Google Maps did not start because billing is not enabled for this API key. Type the pin as latitude, longitude."
      />
    );
  }

  return (
    <APIProvider apiKey={apiKey} language="en" region="SA">
      <PickerBody {...props} />
    </APIProvider>
  );
};

const PickerBody = ({ value, biasQuery = "", onChange }) => {
  const saved = parseLatLng(value);
  const savedLat = saved?.lat;
  const savedLng = saved?.lng;
  const savedKey = saved ? `${saved.lat},${saved.lng}` : "";
  const [preview, setPreview] = useState(null);
  const debouncedBias = useDebounce(biasQuery, 500);
  const geocodingLib = useMapsLibrary("geocoding");

  useEffect(() => {
    if (savedKey || !debouncedBias || !geocodingLib) return undefined;
    let cancelled = false;
    const geocoder = new geocodingLib.Geocoder();
    geocoder.geocode(
      {
        address: `${debouncedBias}, Saudi Arabia`,
        componentRestrictions: { country: "SA" },
      },
      (results, status) => {
        if (cancelled || status !== "OK" || !results?.[0]) return;
        const location = results[0].geometry.location;
        setPreview({ lat: location.lat(), lng: location.lng() });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [savedKey, debouncedBias, geocodingLib]);

  const position = useMemo(() => {
    if (savedLat != null && savedLng != null) return { lat: savedLat, lng: savedLng };
    if (preview) return preview;
    return DEFAULT_CENTER;
  }, [savedLat, savedLng, preview]);

  return (
    <div className="flex flex-col gap-2">
      <AddressSearch
        bias={position}
        onPick={(lat, lng) => onChange(formatLatLng(lat, lng))}
      />
      <MapCanvas position={position} onPick={onChange} />
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {saved
          ? `Pin saved at ${saved.lat}, ${saved.lng}. Drag it or search again to move it.`
          : "Search for a place, click the map, or drag the pin. The location is saved when you do."}
      </p>
    </div>
  );
};

export default MapPinField;
